import { type ApiContainerAction, EnumContainerActionStatus, EnumContainerOrderAction, isBooked } from '@Model';

/** What the correction does to the containers at the site, for the sidebar. */
export interface SiteCorrection {
    /** A note per container whose booking the correction changes, keyed by container id. */
    notes: ReadonlyMap<string, string>;
    /** Booked collections the correction takes back. Their containers stay at the site. */
    staying: ApiContainerAction[];
    /** Booked collections and emptyings the correction gives another container. Until the save they count as open. */
    renumbered: ReadonlySet<string>;
}

/**
 * Compares the booked container actions at the start of editing with the ones now. Only numbered
 * containers are marked, because for a pool container the sidebar does not know which one was booked.
 */
// @ai-generated 2026-10-19T10:54:39+00:00 by philipp.kammerer@synqony.com using Claude Opus 5.5
export function siteCorrectionOf(
    booked: readonly ApiContainerAction[],
    current: readonly ApiContainerAction[],
): SiteCorrection {
    const now = new Map(current.map((action) => [action.id, action]));
    const notes = new Map<string, string>();
    const staying: ApiContainerAction[] = [];
    const renumbered = new Set<string>();

    for (const before of booked.filter(
        (candidate) => isBooked(candidate.booking_status) && candidate.container.foreign_key,
    )) {
        const after = now.get(before.id);
        const renumberedTo = after?.container.foreign_key;
        if ((after && !renumberedTo) || renumberedTo === before.container.foreign_key) {
            continue;
        }

        if (before.action === EnumContainerOrderAction.Place) {
            notes.set(
                before.container.foreign_key!,
                renumberedTo
                    ? `Korrektur: Nr. ${before.container.display_string} → Nr. ${after!.container.display_string}`
                    : 'wird storniert · Korrektur',
            );
            continue;
        }

        if (renumberedTo) {
            renumbered.add(before.id);
        }

        if (before.action === EnumContainerOrderAction.Collect) {
            staying.push(before);
            if (renumberedTo) {
                notes.set(renumberedTo, 'wird abgeholt · Korrektur');
            }
        }
    }

    return { notes, staying, renumbered };
}

/** The container actions as the sidebar matches them: booked ones the correction renumbers count as open again. */
// @ai-generated 2026-10-26T14:02:43+00:00 by philipp.kammerer@synqony.com using Claude Opus 5.5
export function withRenumberedOpen(
    actions: ApiContainerAction[],
    correction: SiteCorrection | undefined,
): ApiContainerAction[] {
    return actions.map((action) =>
        correction?.renumbered.has(action.id)
            ? { ...action, booking_status: EnumContainerActionStatus.Planned }
            : action,
    );
}
